import React, { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { SelectChangeEvent } from '@mui/material/Select';
import dayjs from 'dayjs';
import { lowerCase, startCase } from 'lodash';
import { InputText } from 'app/components/elements/InputText';
import { InputSelect } from 'app/components/elements/InputSelect';
import { DatePicker } from 'app/components/elements/DatePicker';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { dropdownValueTypes } from 'const/option';
import { entriesInclude } from 'utils/entries';
import { getNameOption } from 'utils/convertData';
import { formatAppDateTime } from 'utils/dateFormat';
import type { AssetIssueFormValues } from '../hooks/useAssetIssueDialog';

export const AssetIssueLegacyFields = ({
  form,
  isEditing
}: {
  form: UseFormReturn<AssetIssueFormValues>;
  isEditing: boolean;
}) => {
  const { systemDropdownValues, singleEntryField, users } = useFilteredRoomContext();
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors }
  } = form;

  const reportIssueDropdownOption = useMemo(
    () =>
      systemDropdownValues?.filter((item) =>
        item.options.some((option) => option.type === dropdownValueTypes.reportIssue)
      ) ?? [],
    [systemDropdownValues]
  );

  const reportIssueSingleFields = useMemo(
    () =>
      singleEntryField?.filter((item) =>
        entriesInclude(item.entries, dropdownValueTypes.reportIssue)
      ) ?? [],
    [singleEntryField]
  );

  const readOnly = (label: string, value?: string | null) => (
    <Stack gap={0.5}>
      <Typography fontWeight={700}>{label}</Typography>
      <Typography whiteSpace="pre-line">{value || '-'}</Typography>
    </Stack>
  );

  if (!isEditing) {
    return (
      <Stack gap={2}>
        {readOnly('Equipment ID', watch('equipmentId'))}
        {readOnly('Name', watch('name'))}
        {readOnly('Description', watch('description'))}
        {reportIssueDropdownOption.map((opt) => {
          const val = watch(`option_${opt.id}`);
          const otherVal = watch(`option_${opt.id}_name`);
          const display = val === 'OTHER' ? String(otherVal || '') : String(val || '');
          return (
            <Stack key={opt.id} gap={0.5}>
              <Typography fontWeight={700}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {startCase(lowerCase(getNameOption(opt as any)))}
              </Typography>
              <Typography>{display || '-'}</Typography>
            </Stack>
          );
        })}
        {reportIssueSingleFields.map((field) =>
          readOnly(field.name, String(watch(`single_${field.id}`) || ''))
        )}
        {readOnly(
          'Action By',
          watch('actionBy') === 'OTHER' ? watch('actionByName') : watch('actionBy')
        )}
        {readOnly(
          'Action By Date',
          watch('actionByDate') ? dayjs(watch('actionByDate')).format('YYYY-MM-DD') : '-'
        )}
        {readOnly('Status', watch('status'))}
        {watch('status') === 'RESOLVED' &&
          readOnly(
            'Resolution Date',
            watch('resolutionDate') ? formatAppDateTime(watch('resolutionDate')) : '-'
          )}
        {readOnly('Issue Notes', watch('note'))}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <InputText
        title="Equipment Id"
        inputError={errors.equipmentId?.message}
        placeholder="Enter Equipment Id"
        inputProps={{ ...register('equipmentId') }}
      />
      <InputText
        title="Name"
        inputError={errors.name?.message}
        placeholder="Enter name"
        inputProps={{ ...register('name') }}
      />
      <InputText
        title="Description"
        inputError={errors.description?.message}
        placeholder="Enter Description"
        inputProps={{ ...register('description') }}
      />
      {reportIssueDropdownOption.map((opt) => (
        <React.Fragment key={opt.id}>
          <Controller
            control={control}
            name={`option_${opt.id}`}
            render={({ field: { onChange, value }, fieldState }) => (
              <InputSelect
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                title={`${startCase(lowerCase(getNameOption(opt as any)))}:`}
                inputError={fieldState.error?.message}
                value={value as string}
                placeholder="Select Issue Type"
                onChange={onChange}
                options={opt.dropdownValues?.map((item) => ({
                  label: item.value,
                  value: item.value
                }))}
              />
            )}
          />
          {watch(`option_${opt.id}`) === 'OTHER' && (
            <Controller
              name={`option_${opt.id}_name`}
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  title="Other Issue"
                  placeholder="Enter Other Issue"
                  inputError={fieldState.error?.message}
                  inputProps={{ ...field }}
                />
              )}
            />
          )}
        </React.Fragment>
      ))}
      {reportIssueSingleFields.map((field) => (
        <Stack style={{ width: '100%' }} key={field.id}>
          {field.type === 'DATE' ? (
            <Controller
              name={`single_${field.id}`}
              control={control}
              render={({ field: f }) => (
                <DatePicker
                  title={`${field.name}:`}
                  onChange={(val) =>
                    setValue(`single_${field.id}`, val ? dayjs(val).format('YYYY-MM-DD') : '')
                  }
                  value={f.value ? dayjs(f.value as string) : undefined}
                />
              )}
            />
          ) : (
            <Controller
              name={`single_${field.id}`}
              control={control}
              render={({ field: f, fieldState }) => (
                <InputText
                  title={`${field.name}:`}
                  placeholder={`Enter ${field.name}`}
                  inputProps={{ ...f }}
                  inputError={fieldState.error?.message}
                />
              )}
            />
          )}
        </Stack>
      ))}
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <InputSelect
            onChange={onChange}
            inputError={errors.actionBy?.message || ''}
            title="Action By"
            value={value as string}
            placeholder="Select a user"
            options={[
              ...(users?.map((item) => ({ label: item.label, value: item.label })) ?? []),
              { label: 'Other', value: 'OTHER' }
            ]}
          />
        )}
        name="actionBy"
      />
      {watch('actionBy') === 'OTHER' && (
        <InputText
          title="Action By Name"
          inputError={errors.actionByName?.message}
          placeholder="Enter Action By Name"
          inputProps={{ ...register('actionByName') }}
        />
      )}
      <Controller
        control={control}
        render={({ field: { value } }) => (
          <DatePicker
            inputError={errors.actionByDate?.message}
            style={{ flexDirection: 'column' }}
            title="Choose Action By Date"
            onChange={(val) => setValue('actionByDate', val ? dayjs(val).toISOString() : '')}
            value={value ? dayjs(value as string) : undefined}
          />
        )}
        name="actionByDate"
      />
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <InputSelect
            title="Status"
            inputError={errors.status?.message || ''}
            value={value as string}
            placeholder="Status"
            onChange={(e: SelectChangeEvent<unknown>) => {
              onChange(e.target.value);
              if (e.target.value === 'RESOLVED') {
                setValue('resolutionDate', dayjs().toISOString());
              }
            }}
            options={[
              { value: 'ACTIVE', label: 'Open' },
              { value: 'RESOLVED', label: 'Resolved' }
            ]}
          />
        )}
        name="status"
      />
      {watch('status') === 'RESOLVED' && (
        <Controller
          control={control}
          render={({ field: { value } }) => (
            <DatePicker
              includeTime
              inputError={errors.resolutionDate?.message}
              style={{ flexDirection: 'column' }}
              title="Choose Resolution Date"
              onChange={(val) => {
                const date = dayjs(val);
                setValue('resolutionDate', !val || !date.isValid() ? '' : date.toISOString());
              }}
              value={value ? dayjs(value as string) : undefined}
            />
          )}
          name="resolutionDate"
        />
      )}
      <InputText
        title="Issue Notes"
        inputError={errors.note?.message}
        placeholder="Enter Issue Notes"
        inputProps={{ ...register('note') }}
      />
    </Stack>
  );
};
